import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * The one authored moment per screen, and it belongs to the brand (DESIGN.md §8).
 *
 *   K-05  waiting — the stem grows, the tick draws, both clear, on a 2 s loop.
 *         20 px inline in the button that is waiting; 96 px on a splash.
 *   K-12  PAID — the tick drops onto the stem, once, ~900 ms. Lives on
 *         PerksmithMark's `animated` prop, over the amount due.
 *   K-13  declined — the tick retracts, two arms cross in alert, one shake.
 *
 * The keyframes are in app/globals.css with everything else; these components
 * only choose which one is playing. Reduced motion shows the end state, which
 * the global rule in that file handles for all three at once.
 */
export function CounterLoader({
  size = 20,
  label = "Working",
  onRail = false,
  className,
}: Readonly<{ size?: number; label?: string; onRail?: boolean; className?: string }>) {
  return (
    <span
      role="status"
      aria-live="polite"
      className={cn("perksmith-loader", onRail && "perksmith-loader--on-rail", className)}
    >
      <svg width={size} height={size} viewBox="0 0 64 64" fill="none" aria-hidden="true">
        <rect className="perksmith-loader__stem" x="26" y="26" width="12" height="30" rx="3" />
        <path className="perksmith-loader__tick" d="M22 14l8 8L48 4" />
      </svg>
      <span className="sr-only">{label}…</span>
    </span>
  );
}

/**
 * The outcome mark over the amount due: K-12 on settled, K-13 on declined.
 * A tender is the one moment a counter screen is allowed to celebrate, and it
 * gets exactly one gesture — the mark, not a toast and not a confetti burst.
 */
export function TenderOutcome({
  outcome,
  size = 48,
  className,
}: Readonly<{ outcome: "settled" | "declined"; size?: number; className?: string }>) {
  const settled = outcome === "settled";
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      role="img"
      aria-label={settled ? "Paid" : "Declined"}
      className={cn(
        "shrink-0 text-text-strong",
        settled ? "perksmith-mark--animated" : "perksmith-mark--failed",
        className,
      )}
    >
      <rect className="perksmith-mark__stem" x="26" y="26" width="12" height="30" rx="3" fill="currentColor" />
      {/* A var() in a presentation attribute does not resolve — it has to be a
          class, which is why both arms are styled from globals.css. */}
      <path className="perksmith-mark__tick" d="M22 14l8 8L48 4" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
      {settled ? null : (
        <>
          <path className="perksmith-mark__cross" d="M24 6l16 16" strokeWidth="8" strokeLinecap="round" />
          <path className="perksmith-mark__cross" d="M40 6L24 22" strokeWidth="8" strokeLinecap="round" />
        </>
      )}
    </svg>
  );
}
